import React, { useState } from 'react';

function RichTextEditor({ value, onChange, placeholder }) {
  const [isPreview, setIsPreview] = useState(false);

  const insertLink = () => {
    const url = prompt('Enter URL (e.g., https://example.com):');
    const text = prompt('Enter link text (what users will see):');
    if (url && text) {
      const linkHtml = `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      onChange(value + linkHtml);
    }
  };

  const insertInternalLink = () => {
    const articleId = prompt('Enter article ID (for internal links):');
    const text = prompt('Enter link text:');
    if (articleId && text) {
      const linkHtml = `<a href="/article/${articleId}">${text}</a>`;
      onChange(value + linkHtml);
    }
  };

  const insertActorTag = () => {
    const actorName = prompt('Enter actor/person name:');
    if (actorName) {
      // Create a searchable link that goes to your search page
      const linkHtml = `<a href="/search?q=${encodeURIComponent(actorName)}" class="actor-tag">${actorName}</a>`;
      onChange(value + linkHtml);
    }
  };

  const insertHashtag = () => {
    const tag = prompt('Enter hashtag (without #):');
    if (tag) {
      // Create a searchable hashtag link
      const linkHtml = `<a href="/search?q=${encodeURIComponent('#' + tag)}" class="hashtag">#${tag}</a>`;
      onChange(value + linkHtml);
    }
  };

  const insertCompanyLink = () => {
    const company = prompt('Enter company name (e.g., Netflix, Amazon, Disney):');
    if (company) {
      const linkHtml = `<a href="/search?q=${encodeURIComponent(company)}" class="company-tag">${company}</a>`;
      onChange(value + linkHtml);
    }
  };

  const insertYouTube = () => {
    const url = prompt('Enter YouTube URL:');
    if (url) {
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
      if (videoId) {
        const embedHtml = `
<div class="youtube-embed">
  <iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId[1]}" 
    frameborder="0" allowfullscreen></iframe>
</div>`;
        onChange(value + embedHtml);
      }
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    const alt = prompt('Enter image description:');
    if (url) {
      const imgHtml = `<img src="${url}" alt="${alt || ''}" style="max-width: 100%; height: auto; margin: 15px 0;" />`;
      onChange(value + imgHtml);
    }
  };

  const uploadAndInsertImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      try {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(file);
        });
        
        const uploadResponse = await fetch(`${process.env.REACT_APP_AWS_API_URL}/upload-image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image: base64,
            fileName: file.name,
            contentType: file.type
          })
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          if (uploadResult.success) {
            const alt = prompt('Enter image description (optional):') || '';
            const imgHtml = `<img src="${uploadResult.imageUrl}" alt="${alt}" style="max-width: 100%; height: auto; margin: 15px 0;" />`;
            onChange(value + imgHtml);
            alert('✅ Image uploaded and inserted!');
          } else {
            alert('❌ Image upload failed');
          }
        } else {
          alert('❌ Image upload failed');
        }
      } catch (error) {
        alert('❌ Error uploading image: ' + error.message);
      }
    };
    
    input.click();
  };

  const formatText = (tag) => {
    const selectedText = window.getSelection().toString();
    if (selectedText) {
      const formatted = `<${tag}>${selectedText}</${tag}>`;
      onChange(value.replace(selectedText, formatted));
    }
  };

  const addParagraph = () => {
    onChange(value + '\n\n');
  };

  const formatParagraphs = (text) => {
    // Convert line breaks to proper paragraphs
    return text
      .split('\n\n')
      .filter(p => p.trim())
      .map(p => `<p>${p.trim()}</p>`)
      .join('');
  };

  return (
    <div className="rich-editor">
      <div className="editor-toolbar">
        <button type="button" onClick={() => formatText('strong')}>Bold</button>
        <button type="button" onClick={() => formatText('em')}>Italic</button>
        <button type="button" onClick={() => formatText('h3')}>Heading</button>
        <button type="button" onClick={addParagraph}>¶ New Paragraph</button>
        
        <div className="toolbar-separator">|</div>
        
        <button type="button" onClick={insertLink}>🔗 External Link</button>
        <button type="button" onClick={insertInternalLink}>📄 Internal Link</button>
        
        <div className="toolbar-separator">|</div>
        
        <button type="button" onClick={insertActorTag}>👤 Actor/Person</button>
        <button type="button" onClick={insertCompanyLink}>🏢 Company</button>
        <button type="button" onClick={insertHashtag}># Hashtag</button>
        
        <div className="toolbar-separator">|</div>
        
        <button type="button" onClick={insertYouTube}>📺 YouTube</button>
        <button type="button" onClick={insertImage}>🖼️ Image URL</button>
        <button type="button" onClick={uploadAndInsertImage}>📤 Upload Image</button>
        
        <button type="button" onClick={() => setIsPreview(!isPreview)}>
          {isPreview ? 'Edit' : 'Preview'}
        </button>
      </div>
      
      {isPreview ? (
        <div 
          className="editor-preview"
          dangerouslySetInnerHTML={{ 
            __html: value.includes('<p>') ? value : formatParagraphs(value)
          }}
        />
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows="15"
          className="editor-textarea"
        />
      )}
      
      <div className="editor-help">
        <h4>How to use links:</h4>
        <ul>
          <li><strong>External Link:</strong> Links to other websites (opens in new tab)</li>
          <li><strong>Internal Link:</strong> Links to other articles on your site</li>
          <li><strong>Actor/Person:</strong> Creates searchable links for actors, directors, etc.</li>
          <li><strong>Company:</strong> Links for Netflix, Amazon, Disney, etc.</li>
          <li><strong>Hashtag:</strong> Creates clickable hashtags like #Oscars, #Sundance</li>
        </ul>
      </div>
    </div>
  );
}

export default RichTextEditor;